import {ReactNode} from "react";
import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";
import {formatPL, priceM2Format} from "@pg-mono/string-utils";

import {PRICE_STAT_LABEL, PriceStat} from "../../constants/price_stat";
import {TREND_RCN_COLOR, TREND_RP_COLOR} from "../../constants/trend_series_colors";
import {formatTransactionsCountGenitive} from "../../utils/format_transactions_count";
import {capitalize, NO_DATA_LABEL} from "./prices_trend_format";

export interface ActiveTrendPoint {
    timestamp: number;
    isPreliminary: boolean;
    rpValue: number | null;
    rcnValue: number | null;
    rcnCount: number | null;
}

interface Props {
    activePoint: ActiveTrendPoint | null;
    priceStat: PriceStat;
}

// Mobile equivalent of the desktop point tooltip - Highcharts' own floating tooltip is disabled on mobile
// (see PricesTrendChart's tooltip.enabled) in favour of this fixed panel above the legend, since a
// floating box is awkward to hit/read with a finger. Same content and zone rules as that tooltip.
export const PricesTrendMobileDetailsPanel = (props: Props) => {
    const {activePoint, priceStat} = props;

    if (!activePoint) {
        return (
            <div className={hintStyle}>
                <TapIcon />
                <Text variant="info_txt_1" as="span" color="var(--colors-gray-600)">
                    Dotknij wykresu, żeby zobaczyć szczegóły miesiąca
                </Text>
            </div>
        );
    }

    const {timestamp, isPreliminary, rpValue, rcnValue, rcnCount} = activePoint;
    const monthLabel = capitalize(formatPL(new Date(timestamp), "LLLL yyyy"));

    return (
        <div className={panelStyle}>
            <div className={headerStyle}>
                <Text variant="body_copy_2" as="span" style={{fontWeight: 600}} color="var(--colors-secondary)">
                    {monthLabel}
                </Text>

                <div className={pillsRowStyle}>
                    <TrendPill>{PRICE_STAT_LABEL[priceStat]}</TrendPill>
                    <TrendPill>{isPreliminary ? "Dane wstępne" : "Pełne dane"}</TrendPill>
                </div>
            </div>

            <div className={rowsStyle}>
                <TrendDetailRow color={TREND_RP_COLOR} label="Cena Ofertowa" value={rpValue} description="Na bazie ofert na portalu rynekpierwotny.pl" />

                <TrendDetailRow
                    color={TREND_RCN_COLOR}
                    label="Transakcyjna (RCN)"
                    value={rcnValue}
                    description={
                        rcnCount !== null ? (
                            <>
                                na bazie {isPreliminary ? "tylko " : ""}
                                <Text as="b" variant="info_txt_1" color="var(--colors-gray-600)" style={{fontWeight: 600}}>
                                    {formatTransactionsCountGenitive(rcnCount)}
                                </Text>
                            </>
                        ) : undefined
                    }
                />
            </div>

            {isPreliminary && (
                <>
                    <div className={dividerStyle} />
                    <Text variant="info_txt_1" as="p" color="var(--colors-gray-600)">
                        RCN dopisuje akty notarialne z opóźnieniem, więc ta wartość może się jeszcze zmienić.
                    </Text>
                </>
            )}
        </div>
    );
};

const TrendPill = (props: {children: string}) => <span className={pillStyle}>{props.children}</span>;

const TrendDetailRow = (props: {color: string; label: string; value: number | null; description?: ReactNode}) => {
    const {color, label, value, description} = props;

    return (
        <div>
            <div className={rowHeadStyle}>
                <span className={rowLabelStyle}>
                    <span className={dotStyle} style={{backgroundColor: color}} />
                    <Text as="span" variant="body_copy_2" color="var(--colors-secondary)">
                        {label}
                    </Text>
                </span>

                <Text as="b" variant="body_copy_2" color="var(--colors-secondary)" style={{fontWeight: 600, whiteSpace: "nowrap"}}>
                    {value === null ? NO_DATA_LABEL : priceM2Format(value, {precision: 0})}
                </Text>
            </div>

            {description && (
                <Text variant="info_txt_1" as="p" color="var(--colors-gray-600)" className={rowDescriptionStyle}>
                    {description}
                </Text>
            )}
        </div>
    );
};

const TapIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g stroke="var(--colors-gray-700)" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 7.333V4a1.333 1.333 0 0 0-2.667 0" />
            <path d="M9.333 6.667V2.667a1.333 1.333 0 0 0-2.666 0V4" />
            <path d="M6.667 6.999V4a1.333 1.333 0 0 0-2.667 0v5.333" />
            <path d="M12 5.333a1.333 1.333 0 0 1 2.667 0v4A5.333 5.333 0 0 1 9.333 14.666H8c-1.867 0-3-.573-3.993-1.56l-2.4-2.4a1.31 1.31 0 0 1 1.86-1.845L4.667 10" />
        </g>
    </svg>
);

const panelStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    backgroundColor: "var(--colors-gray-100)",
    borderRadius: 3,
    p: 2
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1
});

const pillsRowStyle = css({
    display: "flex",
    gap: "0.5rem"
});

// Kept in sync with getTooltipPillHtml's badge style in PricesTrendChart.tsx (the same pill, desktop
// vs mobile variant) - same background/font/color/padding, not a separate look.
const pillStyle = css({
    display: "inline-flex",
    alignItems: "center",
    px: "1rem",
    py: "0.4rem",
    borderRadius: "999px",
    backgroundColor: "var(--colors-beige-subtle)",
    fontSize: "1.106rem",
    fontWeight: 600,
    lineHeight: "1.2rem",
    color: "var(--colors-secondary)",
    whiteSpace: "nowrap"
});

const rowsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5
});

const rowHeadStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5
});

const rowLabelStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "0.6rem"
});

const dotStyle = css({
    width: "0.8rem",
    height: "0.8rem",
    borderRadius: "50%",
    flexShrink: 0
});

const rowDescriptionStyle = css({
    marginLeft: "1.4rem",
    marginTop: "0.2rem"
});

const dividerStyle = css({
    height: "1px",
    backgroundColor: "var(--colors-gray-200)"
});

const hintStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    p: 2,
    borderRadius: 3,
    textAlign: "center"
});
import {ReactNode} from "react";
import {css} from "@panda/css";

import {Text} from "@pg-design/text-module";
import {formatPL, priceM2Format} from "@pg-mono/string-utils";

import {PRICE_STAT_LABEL, PriceStat} from "../../constants/price_stat";
import {TREND_RCN_COLOR, TREND_RP_COLOR} from "../../constants/trend_series_colors";
import {formatTransactionsCountGenitive} from "../../utils/format_transactions_count";
import {capitalize, NO_DATA_LABEL} from "./prices_trend_format";

export interface ActiveTrendPoint {
    timestamp: number;
    isPreliminary: boolean;
    rpValue: number | null;
    rcnValue: number | null;
    rcnCount: number | null;
}

interface Props {
    activePoint: ActiveTrendPoint | null;
    priceStat: PriceStat;
}

// Mobile equivalent of the desktop point tooltip - Highcharts' own floating tooltip is disabled on mobile
// (see PricesTrendChart's tooltip.enabled) in favour of this fixed panel above the legend, since a
// floating box is awkward to hit/read with a finger. Same content and zone rules as that tooltip.
export const PricesTrendMobileDetailsPanel = (props: Props) => {
    const {activePoint, priceStat} = props;

    if (!activePoint) {
        return (
            <div className={hintStyle}>
                <TapIcon />
                <Text variant="info_txt_1" as="span" color="var(--colors-gray-600)">
                    Dotknij wykresu, żeby zobaczyć szczegóły miesiąca
                </Text>
            </div>
        );
    }

    const {timestamp, isPreliminary, rpValue, rcnValue, rcnCount} = activePoint;
    const monthLabel = capitalize(formatPL(new Date(timestamp), "LLLL yyyy"));

    return (
        <div className={panelStyle}>
            <div className={headerStyle}>
                <Text variant="body_copy_2" as="span" style={{fontWeight: 600}} color="var(--colors-secondary)">
                    {monthLabel}
                </Text>

                <div className={pillsRowStyle}>
                    <TrendPill>{PRICE_STAT_LABEL[priceStat]}</TrendPill>
                    <TrendPill>{isPreliminary ? "Dane wstępne" : "Pełne dane"}</TrendPill>
                </div>
            </div>

            <div className={rowsStyle}>
                <TrendDetailRow color={TREND_RP_COLOR} label="Cena Ofertowa" value={rpValue} description="Na bazie ofert na portalu rynekpierwotny.pl" />

                <TrendDetailRow
                    color={TREND_RCN_COLOR}
                    label="Transakcyjna (RCN)"
                    value={rcnValue}
                    description={
                        rcnCount !== null ? (
                            <>
                                na bazie {isPreliminary ? "tylko " : ""}
                                <Text as="b" variant="info_txt_1" color="var(--colors-gray-600)" style={{fontWeight: 600}}>
                                    {formatTransactionsCountGenitive(rcnCount)}
                                </Text>
                            </>
                        ) : undefined
                    }
                />
            </div>

            {isPreliminary && (
                <>
                    <div className={dividerStyle} />
                    <Text variant="info_txt_1" as="p" color="var(--colors-gray-600)">
                        RCN dopisuje akty notarialne z opóźnieniem, więc ta wartość może się jeszcze zmienić.
                    </Text>
                </>
            )}
        </div>
    );
};

const TrendPill = (props: {children: string}) => <span className={pillStyle}>{props.children}</span>;

const TrendDetailRow = (props: {color: string; label: string; value: number | null; description?: ReactNode}) => {
    const {color, label, value, description} = props;

    return (
        <div>
            <div className={rowHeadStyle}>
                <span className={rowLabelStyle}>
                    <span className={dotStyle} style={{backgroundColor: color}} />
                    <Text as="span" variant="body_copy_2" color="var(--colors-secondary)">
                        {label}
                    </Text>
                </span>

                <Text as="b" variant="body_copy_2" color="var(--colors-secondary)" style={{fontWeight: 600, whiteSpace: "nowrap"}}>
                    {value === null ? NO_DATA_LABEL : priceM2Format(value, {precision: 0})}
                </Text>
            </div>

            {description && (
                <Text variant="info_txt_1" as="p" color="var(--colors-gray-600)" className={rowDescriptionStyle}>
                    {description}
                </Text>
            )}
        </div>
    );
};

const TapIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g stroke="var(--colors-gray-700)" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 7.333V4a1.333 1.333 0 0 0-2.667 0" />
            <path d="M9.333 6.667V2.667a1.333 1.333 0 0 0-2.666 0V4" />
            <path d="M6.667 6.999V4a1.333 1.333 0 0 0-2.667 0v5.333" />
            <path d="M12 5.333a1.333 1.333 0 0 1 2.667 0v4A5.333 5.333 0 0 1 9.333 14.666H8c-1.867 0-3-.573-3.993-1.56l-2.4-2.4a1.31 1.31 0 0 1 1.86-1.845L4.667 10" />
        </g>
    </svg>
);

const panelStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    backgroundColor: "var(--colors-gray-100)",
    borderRadius: 3,
    p: 2
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1
});

const pillsRowStyle = css({
    display: "flex",
    gap: "0.5rem"
});

// Kept in sync with getTooltipPillHtml's badge style in PricesTrendChart.tsx (the same pill, desktop
// vs mobile variant) - same background/font/color/padding, not a separate look.
const pillStyle = css({
    display: "inline-flex",
    alignItems: "center",
    px: "1rem",
    py: "0.4rem",
    borderRadius: "999px",
    backgroundColor: "var(--colors-beige-subtle)",
    fontSize: "1.106rem",
    fontWeight: 600,
    lineHeight: "1.2rem",
    color: "var(--colors-secondary)",
    whiteSpace: "nowrap"
});

const rowsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5
});

const rowHeadStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1.5
});

const rowLabelStyle = css({
    display: "flex",
    alignItems: "center",
    gap: "0.6rem"
});

const dotStyle = css({
    width: "0.8rem",
    height: "0.8rem",
    borderRadius: "50%",
    flexShrink: 0
});

const rowDescriptionStyle = css({
    marginLeft: "1.4rem",
    marginTop: "0.2rem"
});

const dividerStyle = css({
    height: "1px",
    backgroundColor: "var(--colors-gray-200)"
});

const hintStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    p: 2,
    borderRadius: 3,
    textAlign: "center"
});
